/**
 * Monaco suggest-widget helpers shared by the two "re-run completion because
 * the schema just arrived" paths in `SqlMonaco.tsx` (the SSTS `ready` /
 * `partial` status and the schema-bus cold→warm re-trigger). Both may only
 * refresh a list the user already has on screen — never open one unasked
 * (2026-09-21: a focused, untouched query tab popped the list the moment its
 * schema snapshot published).
 */

/** The slice of a Monaco editor these helpers read (fakeable in tests). */
export type SuggestWidgetHost = {
  getDomNode?: () => HTMLElement | null;
  getContribution?: (id: string) => unknown;
};

export const SUGGEST_WIDGET_VISIBLE_SELECTOR = '.suggest-widget.visible';

/**
 * Is Monaco's suggest widget currently showing for `editor`?
 *
 * Monaco exposes no public "is suggest open" query, so this reads the
 * widget's `visible` class off the editor's own DOM subtree. The probe is
 * scoped to ONE editor: with `fixedOverflowWidgets` the widget is positioned
 * fixed but still lives under that editor's DOM node, so a list open in
 * another tab's editor never counts.
 */
export function isSuggestWidgetVisible(
  editor: SuggestWidgetHost | null | undefined,
): boolean {
  const dom = editor?.getDomNode?.();
  return Boolean(dom?.querySelector(SUGGEST_WIDGET_VISIBLE_SELECTOR));
}

/** Monaco's `SuggestController.ID`. */
export const SUGGEST_CONTROLLER_ID = 'editor.contrib.suggestController';

/** `SuggestModel.state`: 0 idle, 1 manual (Ctrl+Space), 2 auto (quick suggest). */
const SUGGEST_STATE_IDLE = 0;
const SUGGEST_STATE_AUTO = 2;

type SuggestSession = {
  readonly state: number;
  trigger: (options: { auto: boolean; retrigger: boolean }) => void;
};

/**
 * Re-run the completion providers for the suggest session open in `editor`,
 * keeping the list on screen while the fresh items load. Returns false when
 * no session is open or the controller is unavailable.
 *
 * Why not `editor.trigger(…, 'editor.action.triggerSuggest')`: that action's
 * precondition includes "suggest widget NOT visible" (Monaco 0.44
 * `TriggerSuggestAction`), so with a list open the call is a silent no-op —
 * it can only OPEN a closed list, which is the unsolicited popup both callers
 * must never produce. Monaco's own "providers changed" path re-runs an open
 * session through `SuggestModel.trigger({ retrigger: true })`
 * (`_updateActiveSuggestSession`); this does the same through the controller
 * contribution, the way `insertSnippet` in `SqlMonaco.tsx` reaches
 * `snippetController2`. `retrigger: true` is what keeps the widget from being
 * hidden between the cancel and the new items (`SuggestController` hides it
 * only on a non-retrigger cancel). Internal API, pinned to the bundled
 * Monaco 0.44 — the shape is asserted in `suggestWidget.test.ts`.
 */
export function rerunSuggestSession(
  editor: SuggestWidgetHost | null | undefined,
): boolean {
  const controller = editor?.getContribution?.(SUGGEST_CONTROLLER_ID) as
    | { model?: Partial<SuggestSession> }
    | null
    | undefined;
  const session = controller?.model;
  if (!session || typeof session.trigger !== 'function') return false;
  if (session.state === undefined || session.state === SUGGEST_STATE_IDLE) return false;
  session.trigger({ auto: session.state === SUGGEST_STATE_AUTO, retrigger: true });
  return true;
}
